import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  FileText,
  Upload,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Clock,
  Banknote,
  History,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DistanceReviewPanel } from '@/components/admin/DistanceReviewPanel';
import { RemunerationAuditPanel } from '@/components/admin/RemunerationAuditPanel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  pending: { label: 'En attente de batch', color: 'bg-zinc-700 text-zinc-200' },
  sepa_batch_generated: { label: 'Fichier généré', color: 'bg-blue-500/20 text-blue-300 border border-blue-500/40' },
  bank_submitted: { label: 'Transmis à la banque', color: 'bg-amber-500/20 text-amber-300 border border-amber-500/40' },
  paid: { label: 'Confirmé payé', color: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' },
  failed: { label: 'Échec', color: 'bg-red-500/20 text-red-300 border border-red-500/40' },
  mixed: { label: 'Statuts mixtes', color: 'bg-purple-500/20 text-purple-300 border border-purple-500/40' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_LABEL[status] || { label: status || 'inconnu', color: 'bg-zinc-700 text-zinc-200' };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}
      data-testid={`status-badge-${status}`}
    >
      {cfg.label}
    </span>
  );
};

const SepaAdminPage = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [uploadDialog, setUploadDialog] = useState({ open: false, batchId: null });
  const [uploadRef, setUploadRef] = useState('');
  const [uploadNote, setUploadNote] = useState('');

  const [confirmDialog, setConfirmDialog] = useState({ open: false, batchId: null, driverId: null, driverName: '', amount: 0 });
  const [confirmRef, setConfirmRef] = useState('');
  const [confirmNote, setConfirmNote] = useState('');
  const [sendEmail, setSendEmail] = useState(true);

  const [failedDialog, setFailedDialog] = useState({ open: false, batchId: null, driverId: null, driverName: '' });
  const [failedReason, setFailedReason] = useState('');
  const [resetToPending, setResetToPending] = useState(true);

  const [auditDialog, setAuditDialog] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/sepa/batches`, { headers: getAuthHeaders() });
      setBatches(r.data.batches || []);
    } catch (e) {
      toast.error('Échec du chargement des batchs SEPA');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const fetchBatchDetail = async (batchId) => {
    setDetailLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/sepa/batches/${batchId}`, { headers: getAuthHeaders() });
      setBatchDetail(r.data);
    } catch (e) {
      toast.error('Échec du chargement du détail');
      console.error(e);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleBatch = (batchId) => {
    if (expandedBatch === batchId) {
      setExpandedBatch(null);
      setBatchDetail(null);
    } else {
      setExpandedBatch(batchId);
      fetchBatchDetail(batchId);
    }
  };

  // --- Actions -----------------------------------------------------------
  const doConfirmUpload = async () => {
    try {
      const r = await axios.post(
        `${API}/api/admin/sepa/batches/${uploadDialog.batchId}/confirm-upload`,
        { bank_reference: uploadRef, note: uploadNote },
        { headers: getAuthHeaders() },
      );
      toast.success(`Upload bancaire confirmé — ${r.data.earnings_transitioned} earnings → bank_submitted`);
      setUploadDialog({ open: false, batchId: null });
      setUploadRef('');
      setUploadNote('');
      await fetchBatches();
      if (expandedBatch) await fetchBatchDetail(expandedBatch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la confirmation d\'upload');
    }
  };

  const doConfirmPayment = async () => {
    try {
      const r = await axios.post(
        `${API}/api/admin/sepa/batches/${confirmDialog.batchId}/drivers/${confirmDialog.driverId}/confirm-payment`,
        { bank_reference: confirmRef, note: confirmNote, send_email: sendEmail },
        { headers: getAuthHeaders() },
      );
      toast.success(
        `Paiement confirmé pour ${confirmDialog.driverName} — ${r.data.total_amount_eur} € — ${r.data.email_sent ? 'mail envoyé' : 'sans mail'}`,
      );
      setConfirmDialog({ open: false, batchId: null, driverId: null, driverName: '', amount: 0 });
      setConfirmRef('');
      setConfirmNote('');
      setSendEmail(true);
      await fetchBatches();
      if (expandedBatch) await fetchBatchDetail(expandedBatch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la confirmation de paiement');
    }
  };

  const doMarkFailed = async () => {
    if (!failedReason.trim()) {
      toast.error('Motif obligatoire');
      return;
    }
    try {
      const r = await axios.post(
        `${API}/api/admin/sepa/batches/${failedDialog.batchId}/drivers/${failedDialog.driverId}/mark-failed`,
        { reason: failedReason, reset_to_pending: resetToPending },
        { headers: getAuthHeaders() },
      );
      toast.success(
        `Échec enregistré pour ${failedDialog.driverName} — nouveau statut : ${r.data.new_status}`,
      );
      setFailedDialog({ open: false, batchId: null, driverId: null, driverName: '' });
      setFailedReason('');
      setResetToPending(true);
      await fetchBatches();
      if (expandedBatch) await fetchBatchDetail(expandedBatch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'enregistrement');
    }
  };

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/sepa/audit/legacy-paid-without-proof`, { headers: getAuthHeaders() });
      setAuditData(r.data);
      setAuditDialog(true);
    } catch (e) {
      toast.error('Échec de l\'audit');
    } finally {
      setAuditLoading(false);
    }
  };

  // --- Rendering ---------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100 p-6" data-testid="sepa-admin-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" data-testid="back-to-admin-btn">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Admin
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#FFD60A]" />
                Suivi SEPA
              </h1>
              <p className="text-sm text-zinc-400">Pilotage des règlements partenaires — préparation, transmission bancaire, confirmation de crédit.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAudit}
              disabled={auditLoading}
              data-testid="audit-legacy-btn"
            >
              {auditLoading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <History className="w-4 h-4 mr-1" />}
              Audit historique
            </Button>
            <Button size="sm" onClick={fetchBatches} disabled={loading} data-testid="refresh-batches-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Rafraîchir
            </Button>
          </div>
        </div>

        {/* Rémunération mutualisée : modèle, audit, relevés hebdomadaires */}
        <RemunerationAuditPanel />

        {/* Distances ORS à vérifier (bloquent le SEPA) */}
        <DistanceReviewPanel />

        {/* Explain banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 text-sm text-blue-200">
          <p className="font-semibold mb-1 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Cycle de règlement en 4 étapes
          </p>
          <ol className="list-decimal ml-5 space-y-1 text-blue-300/90">
            <li><b>Fichier généré</b> : le XML SEPA est préparé (automatique le lundi ou manuel). Le chauffeur reçoit un courriel « Règlement préparé » (pas d'annonce de paiement).</li>
            <li><b>Transmis à la banque</b> : l'admin a uploadé le XML dans l'espace bancaire. Aucun mail envoyé au chauffeur à cette étape.</li>
            <li><b>Confirmé payé</b> : l'admin vérifie que le crédit a été reçu, puis confirme individuellement. Un courriel « Règlement confirmé » est envoyé au chauffeur.</li>
            <li><b>Échec</b> : rejet bancaire ou IBAN invalide — le montant repart « en attente » pour un prochain batch.</li>
          </ol>
        </div>

        {/* Batches list */}
        {loading ? (
          <div className="text-center py-16 text-zinc-500">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Chargement des batchs…
          </div>
        ) : batches.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 bg-[#18181B] rounded-lg">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
            Aucun batch SEPA pour le moment.
          </div>
        ) : (
          <div className="space-y-2" data-testid="batches-list">
            {batches.map((b) => (
              <BatchRow
                key={b.id}
                batch={b}
                expanded={expandedBatch === b.id}
                onToggle={() => toggleBatch(b.id)}
                detail={expandedBatch === b.id ? batchDetail : null}
                detailLoading={expandedBatch === b.id && detailLoading}
                onConfirmUpload={() => setUploadDialog({ open: true, batchId: b.id })}
                onConfirmPayment={(d) => setConfirmDialog({
                  open: true,
                  batchId: b.id,
                  driverId: d.driver_id,
                  driverName: d.driver_name,
                  amount: d.amount_eur,
                })}
                onMarkFailed={(d) => setFailedDialog({
                  open: true,
                  batchId: b.id,
                  driverId: d.driver_id,
                  driverName: d.driver_name,
                })}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dialog: Confirm Upload */}
      <Dialog open={uploadDialog.open} onOpenChange={(o) => !o && setUploadDialog({ open: false, batchId: null })}>
        <DialogContent className="bg-[#18181B] border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-400" />
              Confirmer l'upload bancaire
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Vous attestez avoir importé le fichier XML dans Société Générale Pro. Tous les règlements du batch passent au statut « Transmis à la banque ». Aucun mail n'est envoyé aux chauffeurs à cette étape.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="upload-ref">Référence bancaire (optionnel)</Label>
              <Input
                id="upload-ref"
                value={uploadRef}
                onChange={(e) => setUploadRef(e.target.value)}
                placeholder="Ex: BATCH-SG-20261019-001"
                className="bg-zinc-900 border-zinc-700"
                data-testid="upload-ref-input"
              />
            </div>
            <div>
              <Label htmlFor="upload-note">Note interne (optionnel)</Label>
              <Input
                id="upload-note"
                value={uploadNote}
                onChange={(e) => setUploadNote(e.target.value)}
                placeholder="Ex: Upload fait à 14h32 par Mariam"
                className="bg-zinc-900 border-zinc-700"
                data-testid="upload-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog({ open: false, batchId: null })}>
              Annuler
            </Button>
            <Button
              onClick={doConfirmUpload}
              className="bg-amber-500 text-black hover:bg-amber-400"
              data-testid="confirm-upload-submit"
            >
              Confirmer upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirm Payment */}
      <Dialog open={confirmDialog.open} onOpenChange={(o) => !o && setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogContent className="bg-[#18181B] border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              Confirmer le crédit bancaire reçu
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Vous attestez que <b>{confirmDialog.driverName}</b> a bien reçu <b>{confirmDialog.amount?.toFixed?.(2)} €</b> sur son compte bancaire. Un courriel « Règlement confirmé » lui sera envoyé automatiquement si l'option est cochée.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="confirm-ref">Référence bancaire du crédit</Label>
              <Input
                id="confirm-ref"
                value={confirmRef}
                onChange={(e) => setConfirmRef(e.target.value)}
                placeholder="Ex: VIR-SG-20261019-ADD-001"
                className="bg-zinc-900 border-zinc-700"
                data-testid="confirm-ref-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-note">Note interne (optionnel)</Label>
              <Input
                id="confirm-note"
                value={confirmNote}
                onChange={(e) => setConfirmNote(e.target.value)}
                placeholder="Ex: Vérifié sur relevé SG du 19/10"
                className="bg-zinc-900 border-zinc-700"
                data-testid="confirm-note-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 accent-[#FFD60A]"
                data-testid="send-email-checkbox"
              />
              Envoyer un courriel « Règlement confirmé » au chauffeur
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
              Annuler
            </Button>
            <Button
              onClick={doConfirmPayment}
              className="bg-emerald-500 text-black hover:bg-emerald-400"
              data-testid="confirm-payment-submit"
            >
              Confirmer paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Mark Failed */}
      <Dialog open={failedDialog.open} onOpenChange={(o) => !o && setFailedDialog({ ...failedDialog, open: false })}>
        <DialogContent className="bg-[#18181B] border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-400" />
              Marquer un échec bancaire
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Enregistrez un rejet bancaire pour <b>{failedDialog.driverName}</b>. Par défaut, le montant repart « en attente » pour inclusion dans un prochain batch.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="failed-reason">Motif *</Label>
              <Input
                id="failed-reason"
                value={failedReason}
                onChange={(e) => setFailedReason(e.target.value)}
                placeholder="Ex: IBAN rejeté - compte clôturé"
                className="bg-zinc-900 border-zinc-700"
                data-testid="failed-reason-input"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input
                type="checkbox"
                checked={resetToPending}
                onChange={(e) => setResetToPending(e.target.checked)}
                className="w-4 h-4 accent-[#FFD60A]"
                data-testid="reset-pending-checkbox"
              />
              Replacer en « En attente de batch » pour un futur règlement (recommandé)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFailedDialog({ ...failedDialog, open: false })}>
              Annuler
            </Button>
            <Button
              onClick={doMarkFailed}
              className="bg-red-500 text-white hover:bg-red-400"
              data-testid="mark-failed-submit"
            >
              Enregistrer l'échec
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Audit historique */}
      <Dialog open={auditDialog} onOpenChange={setAuditDialog}>
        <DialogContent className="bg-[#18181B] border-zinc-800 text-zinc-100 max-w-4xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Audit — Règlements historiques marqués « payé » sans preuve bancaire</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Cet audit identifie les règlements qui ont été marqués automatiquement « payé » dès la génération du XML, sans vérification de crédit bancaire. Il est strictement en lecture seule.
            </DialogDescription>
          </DialogHeader>
          {auditData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <StatCard label="Scanné" value={auditData.summary?.total_paid_earnings_scanned} />
                <StatCard label="Migrable" value={auditData.summary?.migrable_count} color="text-amber-400" />
                <StatCard label="Stripe (OK)" value={auditData.summary?.stripe_payout_count} color="text-emerald-400" />
                <StatCard label="Ambigu" value={auditData.summary?.ambiguous_count} color="text-blue-400" />
              </div>
              {auditData.migrable?.length > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-amber-400 mb-2">
                    {auditData.migrable.length} règlement(s) candidat(s) à la reclassification « sepa_batch_generated »
                  </h3>
                  <div className="bg-zinc-900 rounded-lg overflow-hidden text-xs">
                    <table className="w-full">
                      <thead className="bg-zinc-800">
                        <tr>
                          <th className="text-left p-2">Chauffeur</th>
                          <th className="text-left p-2">Mois</th>
                          <th className="text-right p-2">Montant</th>
                          <th className="text-left p-2">Batch SEPA</th>
                          <th className="text-left p-2">Payé le (prétendument)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditData.migrable.map((m, i) => (
                          <tr key={i} className="border-t border-zinc-800">
                            <td className="p-2">{m.driver_name || m.driver_id?.slice(-8)}</td>
                            <td className="p-2">{m.month}</td>
                            <td className="p-2 text-right font-mono">{m.amount_eur?.toFixed?.(2)} €</td>
                            <td className="p-2 font-mono text-zinc-500">{m.sepa_batch_id?.slice(-12)}</td>
                            <td className="p-2 text-zinc-500">{m.paid_at?.slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-zinc-500 mt-3">
                    Pour appliquer la migration, l'exécution doit être validée manuellement via l'endpoint
                    <code className="bg-zinc-800 px-1 rounded mx-1">POST /api/admin/sepa/audit/migrate-approved</code>
                    avec la liste explicite des couples (driver_id, month) autorisés. Pas d'action en lot possible sans arbitrage humain.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-emerald-400">
                  Aucun règlement candidat à la migration. Historique propre.
                </p>
              )}
            </div>
          ) : (
            <p className="text-zinc-500">Chargement…</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditDialog(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- Subcomponents --------------------------------------------------------
const StatCard = ({ label, value, color = 'text-zinc-100' }) => (
  <div className="bg-zinc-900 rounded-lg p-3 text-center">
    <div className={`text-2xl font-bold ${color}`}>{value ?? 0}</div>
    <div className="text-xs text-zinc-500 mt-1">{label}</div>
  </div>
);

const BatchRow = ({ batch, expanded, onToggle, detail, detailLoading, onConfirmUpload, onConfirmPayment, onMarkFailed }) => {
  const statusCounts = batch.status_counts || {};
  const totalEarnings = batch.earnings_count || 0;
  const paidCount = statusCounts.paid || 0;
  const bankSubCount = statusCounts.bank_submitted || 0;
  const batchGenCount = statusCounts.sepa_batch_generated || 0;
  const failedCount = statusCounts.failed || 0;

  const canConfirmUpload = batchGenCount > 0;

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg overflow-hidden" data-testid={`batch-row-${batch.id?.slice(-8)}`}>
      <div
        className="p-4 flex items-center justify-between hover:bg-zinc-900/50 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <div>
            <div className="font-semibold text-sm flex items-center gap-2">
              Batch {batch.week} <span className="text-xs text-zinc-500 font-mono">#{batch.id?.slice(-8)}</span>
            </div>
            <div className="text-xs text-zinc-500">
              {batch.transactions_count} règlement{batch.transactions_count > 1 ? 's' : ''} · {batch.total_amount_eur} € · {batch.filename}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {bankSubCount > 0 && <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 text-xs">{bankSubCount} transmis</Badge>}
          {batchGenCount > 0 && <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40 text-xs">{batchGenCount} prêt{batchGenCount > 1 ? 's' : ''}</Badge>}
          {paidCount > 0 && <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-xs">{paidCount} payé{paidCount > 1 ? 's' : ''}</Badge>}
          {failedCount > 0 && <Badge className="bg-red-500/20 text-red-300 border-red-500/40 text-xs">{failedCount} échec{failedCount > 1 ? 's' : ''}</Badge>}
          {canConfirmUpload && (
            <Button
              size="sm"
              variant="outline"
              className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10"
              onClick={(e) => { e.stopPropagation(); onConfirmUpload(); }}
              data-testid={`confirm-upload-${batch.id?.slice(-8)}`}
            >
              <Upload className="w-3 h-3 mr-1" />
              Upload banque
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-zinc-800 bg-zinc-900/30 p-4" data-testid={`batch-detail-${batch.id?.slice(-8)}`}>
          {detailLoading ? (
            <div className="text-center py-4 text-zinc-500 text-sm"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Chargement…</div>
          ) : detail ? (
            <>
              <div className="text-xs text-zinc-500 mb-3 flex gap-4 flex-wrap">
                <span>Créé : {batch.created_at?.slice(0, 19).replace('T', ' ')}</span>
                {batch.bank_submitted_at && <span>Transmis : {batch.bank_submitted_at?.slice(0, 19).replace('T', ' ')}</span>}
                {batch.bank_reference && <span>Réf : <code className="bg-zinc-800 px-1 rounded">{batch.bank_reference}</code></span>}
              </div>
              <div className="space-y-2">
                {detail.drivers?.map((d) => (
                  <div
                    key={d.driver_id}
                    className="bg-zinc-900 border border-zinc-800 rounded p-3 flex items-center justify-between"
                    data-testid={`driver-row-${d.driver_id?.slice(-8)}`}
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm">{d.driver_name || d.driver_id?.slice(-8)}</div>
                      <div className="text-xs text-zinc-500">
                        {d.driver_email} · {d.iban?.slice(0, 4)}…{d.iban?.slice(-4)} · mois {d.months?.join(', ')}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="font-mono font-bold text-sm text-[#FFD60A]">{d.amount_eur?.toFixed?.(2)} €</div>
                      <StatusBadge status={d.current_status} />
                      {(d.current_status === 'sepa_batch_generated' || d.current_status === 'bank_submitted') && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10 h-8"
                            onClick={() => onConfirmPayment(d)}
                            data-testid={`confirm-pay-${d.driver_id?.slice(-8)}`}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Confirmer paiement
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-400 border-red-500/40 hover:bg-red-500/10 h-8"
                            onClick={() => onMarkFailed(d)}
                            data-testid={`mark-failed-${d.driver_id?.slice(-8)}`}
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Échec
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {detail.history?.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs font-semibold text-zinc-400 mb-2 flex items-center gap-1">
                    <History className="w-3 h-3" />
                    Historique des transitions
                  </h4>
                  <div className="text-xs space-y-1 max-h-40 overflow-auto">
                    {detail.history.map((h, i) => (
                      <div key={i} className="flex gap-3 text-zinc-500 border-l-2 border-zinc-800 pl-2 py-1">
                        <span className="font-mono text-zinc-600">{h.triggered_at?.slice(0, 19).replace('T', ' ')}</span>
                        <span className="text-zinc-400">{h.driver_id?.slice(-8)} / {h.month}</span>
                        <span>
                          <code className="text-zinc-600">{h.from_status}</code> → <code className="text-zinc-300">{h.to_status}</code>
                        </span>
                        <span className="text-zinc-500 truncate flex-1">{h.triggered_by}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-zinc-500">Impossible de charger le détail.</div>
          )}
        </div>
      )}
    </div>
  );
};

export default SepaAdminPage;
